import { createAsyncThunk, createSlice, createEntityAdapter, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatusObject, createRequestStatusObject, buildReducers } from '../utils';
import { API } from 'aws-amplify';
import type { RootState } from '../store';
import {
	Case,
	CaseStatus,
	Triage,
	ConversationDetail,
	AllowedModel,
	TriageModelConfig,
	TriageCategoryConfig,
	TriageCounts,
	CaseAssignee
} from '../../types/case-management';

// ─── Entity adapters ─────────────────────────────────────────────────────────
// Normalized stores keep entity object references stable across polls. Identical
// data upserted via Immer is a no-op, so unchanged rows keep their reference and
// React.memo'd row components skip re-rendering — no wholesale array replacement.

export const casesAdapter = createEntityAdapter<Case>({
	selectId: (c) => c.id,
	sortComparer: (a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')
});

export const triageAdapter = createEntityAdapter<Triage>({
	selectId: (t) => t.conversationId,
	sortComparer: (a, b) => (b.classifiedAt || '').localeCompare(a.classifiedAt || '')
});

// ─── State ───────────────────────────────────────────────────────────────────

export interface CaseManagementState {
	selectedConversation: ConversationDetail | null;
	cases: EntityState<Case>;
	casesTotalCount: number;
	selectedCase: Case | null;
	triageItems: EntityState<Triage>;
	// Dismissed signals are held apart from `triageItems` so the on-demand
	// review list can never be clobbered by the live queue's delta poll, and
	// vice versa [PTEC-745].
	dismissedTriageItems: Triage[];
	triageCounts: TriageCounts | null;
	triageConfig: TriageModelConfig | null;
	triageAllowedModels: AllowedModel[];
	assignees: CaseAssignee[];

	getConversationRequest: RequestStatusObject;
	listCasesRequest: RequestStatusObject;
	getCaseRequest: RequestStatusObject;
	updateCaseRequest: RequestStatusObject;
	listTriageRequest: RequestStatusObject;
	listDismissedTriageRequest: RequestStatusObject;
	getTriageCountsRequest: RequestStatusObject;
	promoteTriageRequest: RequestStatusObject;
	getTriageConfigRequest: RequestStatusObject;
	updateTriageConfigRequest: RequestStatusObject;
	listAssigneesRequest: RequestStatusObject;
}

const initialState: CaseManagementState = {
	selectedConversation: null,
	cases: casesAdapter.getInitialState(),
	casesTotalCount: 0,
	selectedCase: null,
	triageItems: triageAdapter.getInitialState(),
	dismissedTriageItems: [],
	triageCounts: null,
	triageConfig: null,
	triageAllowedModels: [],
	assignees: [],

	getConversationRequest: createRequestStatusObject(),
	listCasesRequest: createRequestStatusObject(),
	getCaseRequest: createRequestStatusObject(),
	updateCaseRequest: createRequestStatusObject(),
	listTriageRequest: createRequestStatusObject(),
	listDismissedTriageRequest: createRequestStatusObject(),
	getTriageCountsRequest: createRequestStatusObject(),
	promoteTriageRequest: createRequestStatusObject(),
	getTriageConfigRequest: createRequestStatusObject(),
	updateTriageConfigRequest: createRequestStatusObject(),
	listAssigneesRequest: createRequestStatusObject()
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const getConversationDetail = createAsyncThunk(
	'caseManagement/getConversationDetail',
	async (conversationId: string) => {
		const response = await API.get('inspirusapi', `/case-management/conversations/${conversationId}`, {
			headers: { 'Content-Type': 'application/json' }
		});
		return response;
	}
);

export const listCases = createAsyncThunk(
	'caseManagement/listCases',
	async (params: { status?: CaseStatus; page?: number; limit?: number; since?: string } = {}) => {
		const { status, page = 1, limit = 20, since } = params;
		const queryStringParameters: Record<string, any> = { page, limit };
		if (status) queryStringParameters.status = status;
		if (since) queryStringParameters.since = since;
		const response = await API.get('inspirusapi', '/case-management/cases', {
			headers: { 'Content-Type': 'application/json' },
			queryStringParameters
		});
		return { ...response, delta: !!since };
	}
);

export const getCase = createAsyncThunk('caseManagement/getCase', async (caseId: string) => {
	const response = await API.get('inspirusapi', `/case-management/cases/${caseId}`, {
		headers: { 'Content-Type': 'application/json' }
	});
	return response;
});

export const updateCase = createAsyncThunk(
	'caseManagement/updateCase',
	async (params: { caseId: string; updates: Partial<Pick<Case, 'status' | 'priority' | 'assignee' | 'notes'>> }) => {
		const response = await API.put('inspirusapi', `/case-management/cases/${params.caseId}`, {
			headers: { 'Content-Type': 'application/json' },
			body: params.updates
		});
		return response;
	}
);

// The dismissed partition is fetched on demand only — it is review material,
// not a live queue, so it is deliberately left out of the polling loop.
export const listDismissedTriage = createAsyncThunk('caseManagement/listDismissedTriage', async () => {
	const response = await API.get('inspirusapi', '/case-management/triage', {
		headers: { 'Content-Type': 'application/json' },
		queryStringParameters: { bucket: 'dismissed' }
	});
	return response as { items: Triage[] };
});

export const getTriageCounts = createAsyncThunk('caseManagement/getTriageCounts', async () => {
	const response = await API.get('inspirusapi', '/case-management/triage-counts', {
		headers: { 'Content-Type': 'application/json' }
	});
	return response as { counts: TriageCounts };
});

export const listTriageItems = createAsyncThunk(
	'caseManagement/listTriageItems',
	async (params: { resolved?: boolean; since?: string } = {}) => {
		const queryStringParameters: Record<string, any> = {};
		if (params.resolved !== undefined) queryStringParameters.resolved = params.resolved;
		if (params.since) queryStringParameters.since = params.since;
		const response = await API.get('inspirusapi', '/case-management/triage', {
			headers: { 'Content-Type': 'application/json' },
			queryStringParameters
		});
		return { ...response, delta: !!params.since };
	}
);

export const promoteTriage = createAsyncThunk(
	'caseManagement/promoteTriage',
	async (params: { conversationId: string }) => {
		const response = await API.post('inspirusapi', `/case-management/triage/${params.conversationId}/promote`, {
			headers: { 'Content-Type': 'application/json' },
			body: {}
		});
		return response as { case: Case; created: boolean };
	}
);

export const getTriageConfig = createAsyncThunk('caseManagement/getTriageConfig', async () => {
	const response = await API.get('inspirusapi', '/case-management/triage-config', {
		headers: { 'Content-Type': 'application/json' }
	});
	return response as { config: TriageModelConfig | null; allowedModels: AllowedModel[] };
});

export const updateTriageConfig = createAsyncThunk(
	'caseManagement/updateTriageConfig',
	async (params: {
		modelId: string;
		maxTokens: number;
		temperature: number;
		categories?: TriageCategoryConfig[];
		fallbackCategory?: string;
	}) => {
		const response = await API.put('inspirusapi', '/case-management/triage-config', {
			headers: { 'Content-Type': 'application/json' },
			body: params
		});
		return response as { config: TriageModelConfig };
	}
);

export const listAssignees = createAsyncThunk('caseManagement/listAssignees', async () => {
	const response = await API.get('inspirusapi', '/case-management/assignees', {
		headers: { 'Content-Type': 'application/json' }
	});
	return response as { assignees: CaseAssignee[] };
});

// ─── Slice ───────────────────────────────────────────────────────────────────

const caseManagementSlice = createSlice({
	name: 'caseManagement',
	initialState,
	reducers: {
		setSelectedConversation: (state, action: PayloadAction<ConversationDetail | null>) => {
			state.selectedConversation = action.payload;
		},
		setSelectedCase: (state, action: PayloadAction<Case | null>) => {
			state.selectedCase = action.payload;
		},
		clearCaseManagement: () => initialState
	},
	extraReducers: (builder) => {
		buildReducers(builder, getConversationDetail, 'getConversationRequest', {
			onFulfilled: (state, payload) => {
				state.selectedConversation = payload;
			}
		});
		buildReducers(builder, listCases, 'listCasesRequest', {
			onFulfilled: (state, payload) => {
				const incoming: Case[] = payload.cases || [];
				if (payload.delta) {
					// Delta poll: merge changed rows; untouched rows keep their reference.
					casesAdapter.upsertMany(state.cases, incoming);
				} else {
					// Full reconcile: merge what the server returned, then drop only the
					// ids that are no longer present. Unchanged rows are never replaced.
					const incomingIds = new Set(incoming.map((c) => c.id));
					const toRemove = (state.cases.ids as string[]).filter((id) => !incomingIds.has(id));
					casesAdapter.upsertMany(state.cases, incoming);
					if (toRemove.length) casesAdapter.removeMany(state.cases, toRemove);
				}
				state.casesTotalCount = payload.totalCount ?? state.cases.ids.length;
			}
		});
		buildReducers(builder, getCase, 'getCaseRequest', {
			onFulfilled: (state, payload) => {
				state.selectedCase = payload;
			}
		});
		buildReducers(builder, updateCase, 'updateCaseRequest', {
			onFulfilled: (state, payload) => {
				state.selectedCase = payload;
				if (state.cases.entities[payload.id]) {
					casesAdapter.upsertOne(state.cases, payload);
				}
			}
		});
		buildReducers(builder, listDismissedTriage, 'listDismissedTriageRequest', {
			onFulfilled: (state, payload) => {
				state.dismissedTriageItems = payload.items || [];
			}
		});
		buildReducers(builder, getTriageCounts, 'getTriageCountsRequest', {
			onFulfilled: (state, payload) => {
				state.triageCounts = payload.counts || null;
			}
		});
		buildReducers(builder, listTriageItems, 'listTriageRequest', {
			onFulfilled: (state, payload) => {
				const incoming: Triage[] = payload.items || [];
				if (payload.delta) {
					// Delta poll: signals are rolling upserts keyed by conversationId.
					triageAdapter.upsertMany(state.triageItems, incoming);
				} else {
					// Full reconcile: merge returned rows, drop only the departed ones.
					const incomingIds = new Set(incoming.map((t) => t.conversationId));
					const toRemove = (state.triageItems.ids as string[]).filter((id) => !incomingIds.has(id));
					triageAdapter.upsertMany(state.triageItems, incoming);
					if (toRemove.length) triageAdapter.removeMany(state.triageItems, toRemove);
				}
			}
		});
		buildReducers(builder, promoteTriage, 'promoteTriageRequest', {
			onFulfilled: (state, payload) => {
				const conversationId = payload.case.conversationId;
				triageAdapter.removeOne(state.triageItems, conversationId);
				if (!state.cases.entities[payload.case.id]) {
					casesAdapter.upsertOne(state.cases, payload.case);
					state.casesTotalCount += 1;
				}
			}
		});
		buildReducers(builder, getTriageConfig, 'getTriageConfigRequest', {
			onFulfilled: (state, payload) => {
				state.triageConfig = payload.config || null;
				state.triageAllowedModels = payload.allowedModels || [];
			}
		});
		buildReducers(builder, updateTriageConfig, 'updateTriageConfigRequest', {
			onFulfilled: (state, payload) => {
				state.triageConfig = payload.config || null;
			}
		});
		buildReducers(builder, listAssignees, 'listAssigneesRequest', {
			onFulfilled: (state, payload) => {
				state.assignees = payload.assignees || [];
			}
		});
	}
});

export const { setSelectedConversation, setSelectedCase, clearCaseManagement } = caseManagementSlice.actions;

// ─── Memoized selectors ──────────────────────────────────────────────────────
// selectAll returns a referentially-stable array while ids/entities are unchanged,
// so consumers don't re-render on an identical poll.

const casesSelectors = casesAdapter.getSelectors((state: RootState) => state.caseManagement.cases);
const triageSelectors = triageAdapter.getSelectors((state: RootState) => state.caseManagement.triageItems);

export const selectAllCases = casesSelectors.selectAll;
export const selectCaseById = casesSelectors.selectById;
export const selectAllTriageItems = triageSelectors.selectAll;
export const selectCasesTotalCount = (state: RootState) => state.caseManagement.casesTotalCount;

export default caseManagementSlice.reducer;
